import axios, { InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { authService } from './authService';

export const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    const token = authService.getToken();
    if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Custom error class for API errors
export class ApiError extends Error {
  status: number;
  data: any;

  constructor(message: string, status: number, data?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

// Error event for components to listen to
export const apiErrorEvent = new EventTarget();

export const dispatchApiError = (message: string, status: number) => {
  apiErrorEvent.dispatchEvent(
    new CustomEvent('apiError', { detail: { message, status } })
  );
};

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      
      // Handle 401 Unauthorized or 403 Forbidden as a forced logout.
      // authService.logout() clears the session and performs a hard reload.
      if (status === 401 || status === 403) {
        // Surface why the user was logged out on the login screen.
        const reason =
          data?.error === 'SESSION_EXPIRED'
            ? data?.message || 'Your session expired. Please log in again.'
            : data?.message || '';
        if (reason) {
          try {
            sessionStorage.setItem('logout_reason', reason);
          } catch {
            // Ignore storage failures (e.g. private mode).
          }
        }
        authService.logout();
        return Promise.reject(new ApiError('Unauthorized', status, data));
      }
      
      // Extract error message
      const message = data?.message || 
        (status === 400 ? 'Bad Request' : 
         status >= 500 ? 'Server error. Please try again later.' : 
         'An error occurred');
      
      // Dispatch error event for UI components to handle
      dispatchApiError(message, status);
      
      // Log error for debugging
      console.error(`[API Error] ${status}: ${message}`, data);
      
      return Promise.reject(new ApiError(message, status, data));
    } else if (error.request) {
      // Network error: backend didn't respond
      const message = 'Network error. Please check your connection.';
      console.error('[API Error] Network error:', error.message);
      dispatchApiError(message, 0);
      
      return Promise.reject(new ApiError(message, 0));
    }
    
    return Promise.reject(error);
  }
);

// No default export, using named export instead